"use client";

import { useLayoutEffect, type RefObject } from "react";

const FOCUSABLE_SELECTOR = [
	'a[href]',
	'area[href]',
	'button:not([disabled])',
	'input:not([disabled]):not([type="hidden"])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'iframe',
	'object',
	'embed',
	'[contenteditable="true"]',
	'[tabindex]:not([tabindex="-1"])',
].join(",");

interface InertState {
	element: HTMLElement;
	value: string | null;
}

interface ModalFocusManagementOptions {
	dialogRef: RefObject<HTMLElement | null>;
	initialFocusRef?: RefObject<HTMLElement | null>;
	returnFocusElement?: HTMLElement | null;
	getFallbackFocusElement?: () => HTMLElement | null;
	enabled?: boolean;
}

function isElementDisabled(element: HTMLElement) {
	return (
		element.matches(":disabled") ||
		element.getAttribute("aria-disabled") === "true"
	);
}

function isElementVisible(element: HTMLElement) {
	if (
		element.hidden ||
		element.closest("[hidden]") ||
		element.closest('[aria-hidden="true"]') ||
		element.closest("[inert]")
	) {
		return false;
	}

	const style = window.getComputedStyle(element);
	return (
		style.display !== "none" &&
		style.visibility !== "hidden" &&
		element.getClientRects().length > 0
	);
}

function isUsableFocusTarget(element: HTMLElement | null): element is HTMLElement {
	return Boolean(
		element &&
			element.isConnected &&
			!isElementDisabled(element) &&
			isElementVisible(element),
	);
}

function getFocusableElements(dialog: HTMLElement) {
	return Array.from(
		dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
	).filter(
		(element) =>
			element.tabIndex >= 0 &&
			!isElementDisabled(element) &&
			isElementVisible(element),
	);
}

function focusElement(element: HTMLElement | null) {
	if (!isUsableFocusTarget(element)) return false;

	element.focus({ preventScroll: true });
	return document.activeElement === element;
}

/**
 * Applies the operational focus behavior that `aria-modal` does not provide:
 * focus containment, background inertness, and deterministic focus return.
 *
 * The opener is supplied explicitly because native `autoFocus` runs before a
 * layout effect could safely discover the element that launched the dialog.
 */
export function useModalFocusManagement({
	dialogRef,
	initialFocusRef,
	returnFocusElement = null,
	getFallbackFocusElement,
	enabled = true,
}: ModalFocusManagementOptions) {
	useLayoutEffect(() => {
		if (!enabled) return;

		const dialog = dialogRef.current;
		if (!dialog) return;
		const activeElementAtOpen = document.activeElement instanceof HTMLElement
			&& !dialog.contains(document.activeElement)
			? document.activeElement
			: null;
		const returnTarget = returnFocusElement ?? activeElementAtOpen;

		const inertStates: InertState[] = [];
		let branch: HTMLElement = dialog;

		while (branch.parentElement) {
			const parent = branch.parentElement;
			for (const sibling of Array.from(parent.children)) {
				if (!(sibling instanceof HTMLElement) || sibling === branch) continue;

				const value = sibling.getAttribute("inert");
				inertStates.push({ element: sibling, value });
				if (value === null) sibling.setAttribute("inert", "");
			}

			branch = parent;
			if (parent === document.body) break;
		}

		let isRedirectingFocus = false;
		let disposed = false;

		const focusInside = (preferLast = false) => {
			if (disposed) return;

			const preferred = initialFocusRef?.current ?? null;
			if (!preferLast && focusElement(preferred)) return;

			const focusableElements = getFocusableElements(dialog);
			const target = preferLast
				? focusableElements.at(-1)
				: focusableElements[0];
			if (focusElement(target ?? null)) return;

			focusElement(dialog);
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key !== "Tab") return;

			const focusableElements = getFocusableElements(dialog);
			if (focusableElements.length === 0) {
				event.preventDefault();
				focusElement(dialog);
				return;
			}

			const first = focusableElements[0];
			const last = focusableElements.at(-1)!;
			const activeElement = document.activeElement;
			const activeIndex = focusableElements.indexOf(
				activeElement as HTMLElement,
			);

			if (activeIndex === -1) {
				event.preventDefault();
				focusElement(event.shiftKey ? last : first);
			} else if (event.shiftKey && activeElement === first) {
				event.preventDefault();
				focusElement(last);
			} else if (!event.shiftKey && activeElement === last) {
				event.preventDefault();
				focusElement(first);
			}
		};

		const handleFocusIn = (event: FocusEvent) => {
			if (
				isRedirectingFocus ||
				(event.target instanceof Node && dialog.contains(event.target))
			) {
				return;
			}

			isRedirectingFocus = true;
			focusInside();
			isRedirectingFocus = false;
		};

		document.addEventListener("keydown", handleKeyDown, true);
		document.addEventListener("focusin", handleFocusIn, true);
		focusInside();

		return () => {
			disposed = true;
			document.removeEventListener("keydown", handleKeyDown, true);
			document.removeEventListener("focusin", handleFocusIn, true);

			for (const { element, value } of inertStates.reverse()) {
				if (value === null) element.removeAttribute("inert");
				else element.setAttribute("inert", value);
			}

			if (focusElement(returnTarget)) return;
			focusElement(getFallbackFocusElement?.() ?? null);
		};
	}, [
		dialogRef,
		enabled,
		getFallbackFocusElement,
		initialFocusRef,
		returnFocusElement,
	]);
}
